import { useMemo } from "react";
import { useWatch } from "react-hook-form";
import currency from "currency.js";
import type { FindPackageByIdData } from "@/app/lib/types/codegen";
import type { BookingData, TravellerType } from "./types";

type TravellerPriceMap = Record<TravellerType, number>;

export function useTripPrice(tripData: FindPackageByIdData) {
  const selectedTravellers = useWatch<BookingData, "travellers">({
    name: "travellers",
  });

  return useMemo(() => {
    const prices: TravellerPriceMap = {
      adultInDoubleRoom: tripData.priceForAdultInADoubleRoom,
      adultInSingleRoom: tripData.priceForAdultInASingleRoom,
      childWithBed: tripData.priceForChildWithBed,
      childWithoutBed: tripData.priceForChildWithoutBed,
      infant: tripData.priceForInfant,
    };

    const discountedPrices: TravellerPriceMap = {
      adultInDoubleRoom: tripData.discountedPriceForAdultInADoubleRoom,
      adultInSingleRoom: tripData.discountedPriceForAdultInASingleRoom,
      childWithBed: tripData.discountedPriceForChildWithBed,
      childWithoutBed: tripData.discountedPriceForChildWithoutBed,
      infant: tripData.discountedPriceForInfant,
    };

    const { totalPrice, totalDiscountedPrice } = Object.entries(
      selectedTravellers,
    ).reduce(
      (acc, [travellerType, count]) => {
        const type = travellerType as TravellerType;

        return {
          totalPrice: currency(prices[type], { precision: 3 })
            .multiply(count)
            .add(acc.totalPrice).value,
          totalDiscountedPrice: currency(discountedPrices[type], {
            precision: 3,
          })
            .multiply(count)
            .add(acc.totalDiscountedPrice).value,
        };
      },
      { totalPrice: 0, totalDiscountedPrice: 0 },
    );

    return {
      totalPrice,
      totalDiscountedPrice,
      totalDiscount: currency(totalPrice, { precision: 3 }).subtract(
        totalDiscountedPrice,
      ).value,
    };
  }, [selectedTravellers, tripData]);
}
